/**
 * TUVI - Acta de Entrega
 * Genera documento legal de entrega del inmueble desde plantilla Word
 */
import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { UPLOAD_URL } from '../../../config';
import './ActaEntrega.css';

export default function ActaEntrega({ proyecto }) {
  const { success, error } = useNotification();
  const [fechaEntrega, setFechaEntrega] = useState(new Date().toISOString().split('T')[0]);
  const [generado, setGenerado] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [actaKey, setActaKey] = useState(null);

  useEffect(() => {
    const verificarActaExistente = async () => {
      if (!proyecto?.proyecto) return;
      const p = proyecto.proyecto;
      const nombre = p.proyecto_nombre || 'proyecto';
      const apartamento = p.apartamento || 'apto';
      try {
        const res = await fetch(`${UPLOAD_URL}/acta-entrega/check/${encodeURIComponent(nombre)}/${encodeURIComponent(apartamento)}`);
        const data = await res.json();
        if (data.success && data.exists && data.key) {
          setActaKey(data.key);
          setGenerado(true);
        }
      } catch (err) {
        console.error('Error verificando acta:', err);
      }
    };
    verificarActaExistente();
  }, [proyecto]);

  if (!proyecto) return null;

  const { proyecto: p, consideraciones } = proyecto;
  const info = p?.infoAdicional || consideraciones || {};
  const cliente = p?.nombre_cliente || '';
  const cedula = p?.cedula || info.cedula_nit || '';
  const proyNombre = p?.proyecto_nombre || '';
  const apto = p?.apartamento || '';
  const municipio = info.ubicacion_detallada || p?.ubicacion_detallada || p?.zona_barrio || 'Medellín';
  const residente = info.residente || p?.residente || '';

  const handleGenerar = async () => {
    setGenerando(true);
    try {
      const fd = new FormData();
      fd.append('proyecto', proyNombre);
      fd.append('apartamento', apto);
      fd.append('propietario', cliente);
      fd.append('cedula', cedula);
      fd.append('municipio', municipio);
      fd.append('residente', residente);
      fd.append('fecha_entrega', fechaEntrega);

      const token = localStorage.getItem('tuvi_token');
      const res = await fetch(`${UPLOAD_URL}/acta-entrega/generar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const result = await res.json();
      if (result.success) {
        setActaKey(result.key);
        setGenerado(true);
        success(result.message || 'Acta de entrega generada');
      } else {
        error(result.message);
      }
    } catch (err) {
      console.error(err);
      error('Error generando acta');
    } finally {
      setGenerando(false);
    }
  };

  const handleDescargar = async () => {
    if (!actaKey) return;
    try {
      const res = await fetch(`${UPLOAD_URL}/acta-entrega/${encodeURIComponent(actaKey)}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = actaKey.endsWith('.pdf') ? 'pdf' : 'docx';
      a.download = `Acta_Entrega_${proyNombre}_${apto}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      error('Error descargando acta');
    }
  };

  const ext = actaKey?.endsWith('.pdf') ? 'PDF' : 'Word';

  return (
    <div className="acta-container">
      <div className="acta-panel-form" style={{ maxWidth: 500, margin: '0 auto' }}>
        <h3 className="acta-form-title">Acta de Entrega</h3>
        <p className="acta-form-subtitle">Documento legal de entrega del inmueble al propietario</p>

        <div className="acta-info-card">
          <div className="acta-info-row"><span className="acta-info-label">Proyecto:</span><span>{proyNombre}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Apartamento:</span><span>{apto}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Propietario:</span><span>{cliente}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Cedula:</span><span>{cedula || 'No registrada'}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Municipio:</span><span>{municipio}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Residente:</span><span>{residente || 'No asignado'}</span></div>
        </div>

        <div className="acta-form-field">
          <label>Fecha de Entrega *</label>
          <input type="date" value={fechaEntrega} onChange={e => { setFechaEntrega(e.target.value); setGenerado(false); }} />
        </div>

        <div className="acta-form-actions">
          <button className="acta-btn acta-btn-generar" onClick={handleGenerar} disabled={generando || !fechaEntrega}>
            {generando ? 'Generando...' : generado ? 'Regenerar Acta' : 'Generar Acta'}
          </button>
          {generado && actaKey && (
            <button className="acta-btn acta-btn-descargar" onClick={handleDescargar}>
              Descargar {ext}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
